import LogoSpinner from '@/components/atoms/LogoSpinner';
import { TInfinitePagination } from '@/services/api';
import { CheckIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, HStack, IconButton, Progress, Text, Tooltip, VStack } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

// Show "Back to top" once this many pages are loaded.
const BACK_TO_TOP_MIN_PAGES = 3;

interface ITablePaginationProps {
  infinitePagination: TInfinitePagination;
  totalItemsLabel?: string;
  onBackToTop?: () => void;
}

const InfiniteTablePagination = ({
  infinitePagination,
  totalItemsLabel = 'Total Items:',
  onBackToTop,
}: ITablePaginationProps) => {
  const { totalItems, loadedItems, loadedPages, totalPages, hasNextPage, isFetchingNextPage, fetchNextPage } =
    infinitePagination;

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.5,
    trackVisibility: true,
    skip: false,
    delay: 100,
  });

  // Re-checked after every fetch, not only when visibility flips: on a tall screen
  // the sentinel can stay in view after a page loads, which used to stall loading.
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);

  const shownItems = totalItems ? Math.min(loadedItems ?? 0, totalItems) : 0;
  const currentPage = totalPages ? Math.min(loadedPages ?? 0, totalPages) : 0;
  // A single page has nothing to page through, so the progress block stays hidden.
  const hasMultiplePages = !!totalPages && totalPages > 1;
  const showBackToTop = !!onBackToTop && (loadedPages ?? 0) >= BACK_TO_TOP_MIN_PAGES;

  return (
    <>
      {/* Sticky so the totals stay visible while scrolling through a long list. */}
      <HStack
        position={'sticky'}
        bottom={'12px'}
        zIndex={1}
        marginTop={'20px'}
        paddingX={'20px'}
        paddingY={'10px'}
        width={'100%'}
        minHeight={'56px'}
        justifyContent={'space-between'}
        gap={'24px'}
        background={'white'}
        border={'1px solid'}
        borderColor={'background.cloudGray'}
        borderRadius={'12px'}
        boxShadow={'0px 4px 16px 0px #0041791A'}
      >
        <Text fontWeight={'bold'} color={'text.darkBlue'} whiteSpace={'nowrap'}>
          {totalItemsLabel} {totalItems}
        </Text>

        {hasMultiplePages && (
          <VStack flex={1} maxWidth={'320px'} spacing={'6px'} alignItems={'stretch'}>
            <Progress
              value={totalItems ? (shownItems / totalItems) * 100 : 0}
              size={'xs'}
              borderRadius={'full'}
              colorScheme={'primary'}
              backgroundColor={'background.cloudGray'}
              aria-label={`${shownItems} of ${totalItems} loaded`}
            />
            <Text fontSize={'12px'} color={'text.mediumGray'} textAlign={'center'} whiteSpace={'nowrap'}>
              {shownItems} loaded · Page {currentPage} of {totalPages}
            </Text>
          </VStack>
        )}

        <HStack gap={'12px'} minWidth={'120px'} justifyContent={'flex-end'}>
          {isFetchingNextPage && (
            <HStack gap={'6px'}>
              <LogoSpinner width="24px" isLoading={isFetchingNextPage} />
              <Text fontSize={'12px'} color={'text.mediumGray'}>
                Loading…
              </Text>
            </HStack>
          )}
          {!hasNextPage && hasMultiplePages && (
            <HStack gap={'6px'}>
              <CheckIcon boxSize={'10px'} color={'extra.teal'} />
              <Text fontSize={'12px'} fontWeight={600} color={'text.mediumBlue'}>
                All loaded
              </Text>
            </HStack>
          )}
          {showBackToTop && (
            <Tooltip label={'Back to top'}>
              <IconButton
                aria-label={'Back to top'}
                icon={<ChevronUpIcon boxSize={'20px'} />}
                size={'sm'}
                borderRadius={'full'}
                backgroundColor={'primary.100'}
                color={'primary.500'}
                _hover={{ backgroundColor: 'primary.200' }}
                onClick={onBackToTop}
              />
            </Tooltip>
          )}
        </HStack>
      </HStack>

      {/* Loading sentinel at the real end of the list (outside the sticky bar). */}
      <Box ref={inViewRef} height={'30px'} width={'100px'} />
    </>
  );
};

export default InfiniteTablePagination;
